/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */

import { getContentFromCMS } from '@services/strapiService'
import { customReplaceAll, fuelStringSubstitution } from '@utils/fuelStringUtils'
import { getFromCache } from '@utils/redis'
import { capitaliseEveryWord, uppercaseFirstLetter } from '@utils/stringUtils'
import { AMETHI, AMETHI_SLASH, commonCities, hyphenatedCities, slugArrayFuel } from '@constants/fuelPageCities'
import { ContentType } from '@enums/ContentType'
import { KeyType } from '@enums/strapiTypesAndOperators'

const cheerio = typeof window === 'undefined' ? global.cheerio : window.cheerio

const GetNav = (breadCrumb, location, fuelType, mappedStateWithCities, parsedLocationIsState, stateCode) => {
  if (!breadCrumb) {
    return null
  }
  const res: any[] = []
  const $ = cheerio.load(breadCrumb)
  const selection = $('li')
  selection.toArray().map((element, i) => {
    res[i] = {}
    const anchor = $(element).find('a')
    let href = anchor.attr('href')
    if (href) {
      href = fuelStringSubstitution(
        href,
        fuelType,
        location,
        false,
        mappedStateWithCities,
        parsedLocationIsState,
        stateCode
      )
      let linkText = $(anchor).text()
      linkText = fuelStringSubstitution(
        linkText,
        fuelType,
        location,
        false,
        mappedStateWithCities,
        parsedLocationIsState,
        stateCode
      )
      res[i].value = linkText
      res[i].url = href
    } else {
      res[i].value = $(element).text()
      res[i].value = fuelStringSubstitution(
        res[i].value,
        fuelType,
        location,
        true,
        mappedStateWithCities,
        parsedLocationIsState,
        stateCode
      )
    }
  })

  return res
}

export async function getAllStatesIndia() {
  const getAllStatesIndiaResponse = await getFromCache('allStatesInfo')
  return getAllStatesIndiaResponse
}

export async function getFuelMappedStatesCities() {
  const mappedStateWithCitiesObj = await getFromCache('mappedStatesCitiesWithFuelData')
  return mappedStateWithCitiesObj
}

export const isParsedLocationState = (slugValue, allStatesResponse) => {
  const stateValue = getStateName(slugValue)
  const parsedStateArray = JSON.parse(allStatesResponse)
  const isState = parsedStateArray?.find(item => item?.state_name === stateValue)
  return !!isState
}

export const getStateName = item => {
  const stateName = item.toLowerCase().includes('d&nh') ? item?.toUpperCase() : capitaliseEveryWord(item)
  return stateName
}

export const getStateFuelPrices = (state, fuelType, data) => {
  state = getStateName(state)
  const stateData = data[state]
  if (!stateData) {
    return
  }

  let totalPrice = 0
  let count = 0

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const city in stateData) {
    const pump = stateData[city]
    const price = pump[`${fuelType}Price`]
    totalPrice += parseFloat(price)
    count++
  }

  if (count === 0) {
    console.info(`No available data for fuel type "${fuelType}" in state "${state}".`)
    return
  }

  const averagePrice = totalPrice / count
  return averagePrice ? averagePrice.toFixed(2) : 0
}

export const getStateAndCity = (cityName, mappedStateWithCities, stateCode, isCity = false) => {
  const isCommonCity = commonCities.some(cityObj => cityName.toLowerCase() in cityObj)
  if (isCommonCity) {
    const matchedCity = commonCities.find(cityObj => cityObj[cityName.toLowerCase()])
    return isCity
      ? cityName.toUpperCase()
      : stateCode
        ? matchedCity?.[cityName.toLowerCase()]?.otherStates
        : matchedCity?.[cityName.toLowerCase()]?.preferredState
  }
  // Loop through the states
  for (const state in mappedStateWithCities) {
    if (mappedStateWithCities[state]) {
      // Get the cities within the current state
      const citiesInState = Object.keys(mappedStateWithCities[state])

      // Loop through the cities
      for (const cityInState of citiesInState) {
        if (cityInState.toLowerCase() === cityName.toLowerCase()) {
          return isCity ? cityInState : state
        }
      }
    }
  }

  return isCity ? 'City not found' : 'State not found'
}

export enum FuelType {
  Petrol = 'petrol',
  Diesel = 'diesel',
  Fuel = 'fuel',
}

export const isValidSlug = (slug, mappedCitiesWithStates) => {
  const validSlugArray = generateValidSlugs(mappedCitiesWithStates)

  slug = customReplaceAll(slug, '%26', '&')
  return validSlugArray?.includes(slug)
}

export const getBreadCrumbResponse = (
  fuelPageResponse,
  location,
  fuelType,
  mappedStateWithCities,
  parsedLocationIsState,
  stateCode
) => {
  const breadCrumbValue = fuelPageResponse?.[0]?.attributes?.breadCrumbs
  const breadCrumbResponse = GetNav(
    breadCrumbValue,
    location,
    fuelType,
    mappedStateWithCities,
    parsedLocationIsState,
    stateCode
  )

  if (!breadCrumbResponse) {
    return null
  }

  return breadCrumbResponse
}

export const convertToStructuredDataFuel = arrayOfObjects =>
  arrayOfObjects.map(obj => ({
    '@type': 'Question',
    name: obj.attributes.name,
    acceptedAnswer: {
      '@type': 'Answer',
      text: obj.attributes.content,
    },
  }))

export function appendCodeToCommonCitiesMultipleStates(
  fuelType,
  state,
  location,
  commonCitiesMultipleStatesMap,
  listOfCities
) {
  const formattedCity = uppercaseFirstLetter(location)
  const formattedLocation = location.replace(/\s+/g, '-').replace(/\//g, '-').toLowerCase()

  const roCodeDerived =
    typeof listOfCities === 'object'
      ? listOfCities[location.toUpperCase()]?.roCode || listOfCities[capitaliseEveryWord(location)]?.roCode
      : listOfCities

  const defaultUrl = `${fuelType}-price-in-${formattedLocation}`

  let finalFuelPagePath

  if (commonCitiesMultipleStatesMap[formattedCity]) {
    finalFuelPagePath =
      commonCitiesMultipleStatesMap[formattedCity]?.preferredState.toUpperCase() === state.toUpperCase()
        ? defaultUrl
        : commonCitiesMultipleStatesMap[formattedCity]?.otherStates.toUpperCase()?.includes(state.toUpperCase())
          ? `${defaultUrl}-${roCodeDerived}`
          : defaultUrl
  } else {
    finalFuelPagePath = defaultUrl
  }

  return finalFuelPagePath
}

export const defaultSelectedState = (
  parsedLocationIsState,
  location,
  mappedStateWithCities,
  fuelStateArray,
  stateCode
) => {
  const setDisplayValue = parsedLocationIsState
    ? capitaliseEveryWord(location)
    : getStateAndCity(location, mappedStateWithCities, stateCode) || ''

  const setValue =
    fuelStateArray.find(
      obj =>
        uppercaseFirstLetter(obj.state_name) ===
        (parsedLocationIsState
          ? uppercaseFirstLetter(location)
          : uppercaseFirstLetter(getStateAndCity(location, mappedStateWithCities, stateCode)))
    )?.state_code || ''

  return { setDisplayValue, setValue }
}

export const defaultSelectedCity = (parsedLocationIsState, location, mappedStateWithCities, stateCode) => {
  const stateFromCity = mappedStateWithCities?.[getStateAndCity(location, mappedStateWithCities, stateCode)]

  const setDisplayCity = parsedLocationIsState ? '' : uppercaseFirstLetter(location) || ''
  const setValue = parsedLocationIsState ? '' : uppercaseFirstLetter(location) || ''
  const petrolPrice = parsedLocationIsState
    ? getStateFuelPrices(location, 'petrol', mappedStateWithCities)
    : stateFromCity?.[getStateAndCity(location, mappedStateWithCities, stateCode, true)]?.petrolPrice
  const dieselPrice = parsedLocationIsState
    ? getStateFuelPrices(location, 'diesel', mappedStateWithCities)
    : stateFromCity?.[getStateAndCity(location, mappedStateWithCities, stateCode, true)]?.dieselPrice

  return { setDisplayCity, setValue, petrolPrice, dieselPrice }
}

export const extractAllChildComponentObjectsFuelCollapse = (data, relation) => {
  const results: any = []

  // Iterate over each item in the data array
  data?.forEach(item => {
    if (item.__component === relation) {
      item.Content.data.forEach(val => {
        results.push(val?.attributes?.collapsibles)
      })
    }
  })

  return results
}

export function isNumeric(n) {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(parseFloat(n)) && isFinite(n)
}

const processLocationName = (location, hyphenatedCities) => {
  let formattedLocation = location.toLowerCase().replace(/\s+/g, '-')

  if (formattedLocation === AMETHI) return AMETHI_SLASH
  if (hyphenatedCities.includes(formattedLocation)) return formattedLocation

  formattedLocation = location
    .split('/')
    .map(part => part.replace(/-/g, ' '))
    .join('/')

  return formattedLocation.includes('d&nh') ? formattedLocation.toUpperCase() : formattedLocation
}

export const getLocationFromUrl = (url, slugLength) => {
  let location = url.split('-')
  location[location.length - 1] = isNumeric(location[location.length - 1]) ? '' : location[location.length - 1]
  location = location.slice(3, slugLength).join(' ').trim()
  location = customReplaceAll(location, '%26', '&')
  location = customReplaceAll(location, '%20', ' ')
  location = processLocationName(location, hyphenatedCities)

  return location
}

export const parseUrl = (url, slugLength) => {
  const stateCode = isNumeric(url.split('-')[slugLength - 1]) ? url.split('-')[slugLength - 1] : ''

  return [url.split('-')[0], stateCode]
}

export const getFuelPageResponse = async (slug, parsedLocationIsState) => {
  let fuelPageResponse = await getContentFromCMS({
    contentType: ContentType.Fuelpage,
    filters: [{ key: KeyType.SLUG, value: slug }],
  })

  if (!fuelPageResponse?.length && parsedLocationIsState) {
    fuelPageResponse = await getContentFromCMS({
      contentType: ContentType.Fuelpage,
      filters: [{ key: KeyType.SLUG, value: 'state-default' }],
    })
  } else if (!fuelPageResponse?.length) {
    fuelPageResponse = await getContentFromCMS({
      contentType: ContentType.Fuelpage,
      filters: [{ key: KeyType.SLUG, value: 'default' }],
    })
  }

  return fuelPageResponse
}

// Average fuel Price for a State

export const getAverageStatePrice = (listAllCitiesInState, fuelName) =>
  listAllCitiesInState &&
  (
    Object.keys(listAllCitiesInState)
      .map(item => Number(listAllCitiesInState[item][`${fuelName}Price`]))
      .reduce((prev, next) => prev + next) / Object.keys(listAllCitiesInState).length
  ).toFixed(2)

export const getFormattedStatesAndCities = mappedCitiesWithStates => {
  const arr: string[] = []

  for (const state in mappedCitiesWithStates) {
    if (state) {
      const formattedState = state.toLowerCase().replace(/ /g, '-')
      arr.push(formattedState)

      const cities = Object.keys(mappedCitiesWithStates[state])
      for (const city of cities) {
        const formattedCity = city.toLowerCase().replace(/ /g, '-')
        arr.push(formattedCity)
      }
    }
  }

  return arr
}

export const generateValidSlugs = mappedCitiesWithStates => {
  let validSlugArray: string[] = []
  validSlugArray = validSlugArray.concat(slugArrayFuel)

  const locationSlug = getFormattedStatesAndCities(mappedCitiesWithStates)

  locationSlug.map(item => {
    validSlugArray.push(`petrol-price-in-${item}`)
    validSlugArray.push(`diesel-price-in-${item}`)
  })

  return validSlugArray
}
